// ── Event logging ────────────────────────────────────────────────────────────
// Fire-and-forget log of what readers do, so /analytics can list it. POSTs to
// the shona-reader-request-log Lambda (through API Gateway; see
// server/lambda/log_handler.py), which writes one JSON object per event to S3
// under a prefix for each kind of event:
//
//   logRequest  — a TTS generation               → request-log/
//   logDownload — a clip a signed-in reader saved → download-log/
//   logError    — a failure the reader was shown → error-log/
//
//   REACT_APP_SHONA_LOG_URL — the API's POST /log route. Unset = logging off.

const LOG_URL = process.env.REACT_APP_SHONA_LOG_URL;

// ── session ID ───────────────────────────────────────────────────────────────
// A guest has no account, so every logged event also carries a session ID: a
// random token, tied to nothing but the browser that generated it, kept in
// localStorage so the same visitor reuses it across visits. It is what turns a
// column of interchangeable "Guest" rows on /analytics into a countable handful
// of people. Signed-in requests carry it too, so an account can be recognised
// as the browser that was browsing anonymously before it signed up.
const SESSION_KEY = 'shonaReaderSessionId';

const randomId = () => {
  const c = typeof crypto !== 'undefined' ? crypto : null;
  if (c?.randomUUID) return c.randomUUID();
  if (c?.getRandomValues) {
    return Array.from(c.getRandomValues(new Uint8Array(16)))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }
  return `${Date.now().toString(16)}${Math.random().toString(16).slice(2)}`;
};

// Also held in memory, so a browser that refuses storage (Safari in private
// mode throws on access) still resolves one visit's requests to one person
// rather than to one person per request.
let sessionId = null;

export const getSessionId = () => {
  if (sessionId) return sessionId;
  sessionId = randomId();
  try {
    const stored = window.localStorage.getItem(SESSION_KEY);
    if (stored) sessionId = stored;
    else window.localStorage.setItem(SESSION_KEY, sessionId);
  } catch {
    // No storage — the in-memory ID lasts until the page unloads.
  }
  return sessionId;
};

// `event` goes last so a payload can never file itself under another kind.
const logEvent = (event, payload) => {
  if (!LOG_URL) return;
  try {
    fetch(LOG_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId: getSessionId(), ...payload, event }),
      // keepalive lets the request survive navigation away from the page.
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Logging must never break the reader.
  }
};

export const logRequest = payload => logEvent('request', payload);
export const logDownload = payload => logEvent('download', payload);
export const logError = payload => logEvent('error', payload);
